import { _, type TranslateService } from '@ngx-translate/core';
import { type DatePreset } from '@sb/ui/date-picker';
import { type DialogConfirmData } from '@sb/ui/dialog';
import {
  addPlainDays,
  addPlainMonths,
  endOfPlainISOWeek,
  endOfPlainMonth,
  fromPlainDate,
  plainDateToday,
  startOfPlainISOWeek,
  startOfPlainMonth,
  toISODateString,
  type PlainDate,
  type PlainDateRange,
} from '@shiftbase-com/ts-shared/utilities';

import { type PayrollRunScope } from './payroll.model';

/**
 * The last day a payroll period may cover. Payroll prepares days that have already happened, so
 * the header's date control offers nothing past today.
 *
 * Each caller reads this once, at construction, matching how `sb-date-range-picker` fixes its own
 * `today` — a session left open across midnight keeps that day as its ceiling until it reloads.
 * The timesheet entry link applies the same ceiling on arrival instead, through
 * `parsePayrollEntryParams`, since its period never passes through a picker at all.
 */
export function payrollPeriodMaxDate(): PlainDate {
  return plainDateToday();
}

/**
 * The named periods the header's date control offers beside its calendar. The picker validates
 * nothing and computes nothing: which periods a screen offers is the screen's own call, so payroll
 * hands over four literal ranges and the copy for each.
 *
 * `today` is the anchor for all four, and the caller passes the same day it caps the picker with —
 * a period resolved against a later day than the picker's own ceiling would render disabled.
 *
 * The week periods are whole ISO weeks. No account setting for the first day of the week exists,
 * and ts-shared's week helpers take no week-start option, so Monday–Sunday is the only reading
 * available.
 */
export function payrollPeriodPresets(
  today: PlainDate,
  translate: (key: string) => string,
): DatePreset<PlainDateRange>[] {
  const previousMonth = addPlainMonths(today, -1);
  const lastWholeWeekEnd = endOfPlainISOWeek(addPlainDays(today, -7));

  return [
    { label: translate(_('Month to date')), value: { start: startOfPlainMonth(today), end: today } },
    {
      label: translate(_('Last month')),
      value: { start: startOfPlainMonth(previousMonth), end: endOfPlainMonth(previousMonth) },
    },
    {
      label: translate(_('Last 2 weeks')),
      value: { start: startOfPlainISOWeek(addPlainDays(today, -14)), end: lastWholeWeekEnd },
    },
    {
      label: translate(_('Last 4 weeks')),
      value: { start: startOfPlainISOWeek(addPlainDays(today, -28)), end: lastWholeWeekEnd },
    },
  ];
}

/**
 * `sb-date-range-picker` speaks `PlainDateRange` at its own boundary; the payroll domain keeps
 * `Date` throughout (`PayrollRunScope.range`, `payroll-sources.ts`'s day-by-day iteration, every
 * existing test). These two convert right at the header control, so nothing else in the payroll
 * code needs to change for the picker's own date representation.
 */
export function toPlainDateRange(range: PayrollRunScope['range']): PlainDateRange {
  return { start: toISODateString(range.start), end: toISODateString(range.end) };
}

export function fromPlainDateRange(range: PlainDateRange): PayrollRunScope['range'] {
  return { start: fromPlainDate(range.start), end: fromPlainDate(range.end) };
}

/**
 * Whether two payroll run scopes describe the same run. Used to decide whether a pick in one of
 * the header's controls actually changed anything — a spurious "changed" here fires the confirm
 * dialog and resets a manager's progress for nothing; a spurious "unchanged" silently drops
 * their edit.
 *
 * Dates compare by whole calendar day, not exact instant. `SbDateRangePickerComponent` always
 * hands back start-of-day `Date`s in the browser's local timezone, so a scope re-picked with no
 * real edit would already compare equal by exact time value too — the day-granular comparison
 * is chosen because a payroll period is itself day-granular (nobody picks a half day), not as a
 * defensive fallback for millisecond drift. Either way, two `Date` instances for the same
 * moment are never `===`, so this always compares by value.
 *
 * `departmentIds` compare order-insensitively — `['a', 'b']` and `['b', 'a']` are the same scope
 * — but length- and duplicate-sensitively: comparing via `Set` would fold `['a', 'a']` and
 * `['a']` into equal sets, so this checks length first and then a sorted-array comparison rather
 * than routing through a `Set`.
 */
export function isSameScope(first: PayrollRunScope, second: PayrollRunScope): boolean {
  return (
    isSameDay(first.range.start, second.range.start) &&
    isSameDay(first.range.end, second.range.end) &&
    isSameDepartmentIds(first.departmentIds, second.departmentIds)
  );
}

function isSameDay(firstDate: Date, secondDate: Date): boolean {
  return (
    firstDate.getFullYear() === secondDate.getFullYear() &&
    firstDate.getMonth() === secondDate.getMonth() &&
    firstDate.getDate() === secondDate.getDate()
  );
}

function isSameDepartmentIds(firstIds: string[], secondIds: string[]): boolean {
  if (firstIds.length !== secondIds.length) {
    return false;
  }

  const sortedFirst = firstIds.toSorted();
  const sortedSecond = secondIds.toSorted();

  return sortedFirst.every((departmentId, index) => departmentId === sortedSecond[index]);
}

/**
 * The warning shown before the header lets a manager change a run's scope (AC 6). Approvals and
 * locks are saved on their own days, so the warning is about the run's progress, not about data —
 * which is why the active step is asked about its own unsaved work separately, before this.
 */
export function payrollScopeChangeConfirm(translate: TranslateService): DialogConfirmData {
  return {
    title: translate.instant(_('Change scope?')),
    description: translate.instant(
      _(
        "The steps track the current payroll scope. If you change it, they point at the new payroll scope — approvals and locks you've already given stay saved on their own days.",
      ),
    ),
    type: 'warning',
    primary: { text: translate.instant(_('Continue')) },
  };
}
